import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useContext,
  ReactNode,
} from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button, TabContent, TabPane } from 'reactstrap';
import { useNavigate } from 'react-router';
import { Operation } from '../../../../../../services/types/Operation';
import { Modal, SpinnerArea, TabBar } from '../../../../../../shared/components';
import MainContext from '../../../../../App/MainProvider/context';

// react/no-unused-prop-types no traza el uso a traves de los sectores concretos
// (UrbanInfrastructureTool/SectorTabContainer.tsx, TransportAndRoadRehabilitationTool/
// SectorTabContainer.tsx), que extienden esta interfaz y leen calculatorId/
// totalConstructionOverriden/totalOperationOverriden/setTotalConstructionOverriden/
// setTotalOperationOverriden de baseProps - SectorTabContainerBase en si no los
// destructura, pero son props reales y usadas, no deuda.
/* eslint-disable react/no-unused-prop-types */
export interface SectorTabContainerBaseProps {
  calculatorId: number;
  operation: Operation;
  loading: boolean;
  isSaveDisabled: boolean;
  totalConstructionOverriden: number | null;
  totalOperationOverriden: number | null;
  isFinished: boolean;
  setIsSaveDisabled: (disabled: boolean) => void;
  setTotalConstructionOverriden: React.Dispatch<React.SetStateAction<number | null>>;
  setTotalOperationOverriden: React.Dispatch<React.SetStateAction<number | null>>;
}
/* eslint-enable react/no-unused-prop-types */

export interface TabContentBase {
  constructionTab: ReactNode;
  operationTab: ReactNode;
  summaryTab: ReactNode;
}

export interface SectorTabContainerBaseHandlers {
  // El DTO real difiere por sector concreto (CalculatorTransportDto, CalculatorUrbanDto, ...);
  // este handler nunca se invoca dentro de SectorTabContainerBase, así que no hay una forma
  // común real que tipar aquí sin acoplar el base a un sector específico.
  buildDto: () => unknown;
  saveChanges: () => Promise<void>;
  finishCalculator: () => Promise<void>;
  updateInitialValues: () => void;
  hasChanges: () => boolean;
  checkForNullValues: () => boolean;
}

export interface SectorTabContainerBaseHandle {
  saveChanges: () => Promise<void>;
}

interface Props extends SectorTabContainerBaseProps {
  tabContent: TabContentBase;
  handlers: SectorTabContainerBaseHandlers;
}

const SectorTabContainerBase = forwardRef<SectorTabContainerBaseHandle, Props>(
  (
    {
      operation,
      loading,
      isSaveDisabled,
      isFinished: initialIsFinished,
      setIsSaveDisabled,
      tabContent,
      handlers,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState(1);

    const [isFinished] = useState<boolean>(initialIsFinished);

    const [saving, setSaving] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [modalFinishVisible, setModalFinishVisible] = useState(false);
    const [modalErrorVisible, setModalErrorVisible] = useState(false);

    const navigate = useNavigate();
    const { notify, service } = useContext(MainContext);

    const tabHeaders = [
      { id: 1, text: t('construction'), htmlId: 'tabConstruction' },
      { id: 2, text: t('operation'), htmlId: 'tabOperation' },
      { id: 3, text: t('emissionsSummary'), htmlId: 'tabEmissionsSummary' },
    ];

    const toggle = (tabId: number) => {
      if (activeTab !== tabId) {
        setActiveTab(tabId);
      }
    };

    const handleNext = () => {
      if (activeTab < tabHeaders.length) {
        setActiveTab((prevTab) => prevTab + 1);
      }
    };

    const handlePrevious = () => {
      if (activeTab > 1) {
        setActiveTab((prevTab) => prevTab - 1);
      }
    };

    // Función para ejecutar el servicio y realizar las acciones de callback.
    const handleServiceAction = (
      serviceMethod: () => Promise<void>,
      successMessage: string,
      callback: () => void
    ) =>
      service(serviceMethod, {
        callback: () => {
          notify(t('GHG Calculator'), t(successMessage), 'success');
          handlers.updateInitialValues();
          // flushSync fuerza el re-render de este cambio de estado ANTES de seguir
          // (en vez del batching normal, diferido a un microtask). Es necesario
          // porque saveDataLeaving()/setFinishCalculator() navegan justo después de
          // este callback: RouteLeavingGuard usa react-router's useBlocker, que evalúa
          // su predicado contra el `when`/`shouldBlockNavigation` YA RENDERIZADOS -
          // sin este flushSync, isSaveDisabled seguía siendo `false` (el valor del
          // render anterior) en el momento exacto del navigate(), y la navegación
          // quedaba bloqueada en silencio (bug real encontrado en QA manual tras
          // F4-4). Confirmado con logs reales que un `useEffect` diferido tampoco
          // alcanza acá: el árbol de Calculator se remonta (pierde todo estado local)
          // apenas termina un guardado exitoso - la causa exacta de ese remount no
          // se identificó, pero cualquier fix basado en estado local diferido a un
          // effect es vulnerable a perderse en ese remount. flushSync navega en el
          // mismo tick, sin depender de sobrevivir a un re-render futuro.
          flushSync(() => {
            setIsSaveDisabled(true);
          });
          callback();
        },
        loader: setSaving,
      });

    const saveChanges = () =>
      handleServiceAction(
        () => handlers.saveChanges(),
        'savedSuccessfully',
        () => {}
      );

    const finishCalculator = () =>
      handleServiceAction(
        () => handlers.finishCalculator(),
        'finishedCalculator',
        () => {}
      );

    // Exponer la función saveChanges para que sea accesible desde el padre.
    useImperativeHandle(ref, () => ({
      saveChanges,
    }));

    // No envolver saveChanges()/finishCalculator() en otro handleServiceAction: son
    // funciones "fire and forget" (disparan su propio service() interno sin devolver
    // esa promesa), así que un `async () => saveChanges()` externo resolvía apenas
    // terminaba la parte síncrona - antes de que el guardado real terminara. Acá se
    // espera la promesa real (ya devuelta correctamente por saveChanges/
    // finishCalculator) y se navega justo después: para cuando esta línea corre,
    // isSaveDisabled ya quedó en `true` de forma síncrona (flushSync en
    // handleServiceAction), así que RouteLeavingGuard ya no bloquea.
    const saveDataLeaving = async () => {
      await saveChanges();
      navigate(`/operations/${operation.number}/calculator`);
    };

    const setFinishCalculator = async () => {
      await finishCalculator();
      navigate(`/operations/${operation.number}/calculator`);
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    };

    const leaveWithOutSave = () => {
      flushSync(() => {
        setIsSaveDisabled(true);
      });
      navigate(`/operations/${operation.number}/calculator`);
    };

    const actions = [
      { text: t('cancel') },
      { text: t('dontSave'), action: () => leaveWithOutSave() },
      { text: t('saveChanges'), action: () => saveDataLeaving() },
    ];

    const actionsNoFinish = [{ text: t('Ok') }];

    const actionsFinish = [
      { text: t('cancel') },
      { text: t('Finish'), action: () => setFinishCalculator() },
    ];

    const cancelHandler = () => {
      if (isSaveDisabled) {
        setIsSaveDisabled(true);
        navigate(`/operations/${operation.number}/calculator`);
      }

      setModalVisible(true);
    };

    useEffect(() => {
      const hasChanges = handlers.hasChanges();
      setIsSaveDisabled(!hasChanges);
    }, [handlers, setIsSaveDisabled]);

    return (
      <SpinnerArea loading={loading || saving}>
        {operation && (
          <>
            <TabBar
              id="operationGeneralInformation"
              tabSelected={activeTab}
              setTabSelected={toggle}
              headers={tabHeaders}
            />
            <TabContent activeTab={activeTab}>
              {tabHeaders.map((tab) => (
                <TabPane key={tab.id} tabId={tab.id}>
                  {tab.id === 1 && tabContent.constructionTab}
                  {tab.id === 2 && tabContent.operationTab}
                  {tab.id === 3 && tabContent.summaryTab}
                </TabPane>
              ))}
            </TabContent>
            <div className="calculator__footer">
              <Button
                type="submit"
                color="secondary"
                itemID="btn_cancel"
                onClick={() => cancelHandler()}
                className="calculator-BtnCancel"
              >
                {t('Cancel')}
              </Button>{' '}
              {activeTab > 1 && (
                <Button
                  className="btn-tertiary prev-calc"
                  type="button"
                  onClick={handlePrevious}
                  icon="chevron-left"
                >
                  {t('Previous')}
                </Button>
              )}
              <Button
                type="submit"
                color="secondary"
                itemID="btn_saveChanges"
                onClick={() => {
                  saveChanges();
                }}
                disabled={isSaveDisabled}
              >
                {t('save')}
              </Button>{' '}
              {activeTab < tabHeaders.length && (
                <Button type="button" color="primary" onClick={handleNext} itemID="btn_nextTab">
                  Next
                </Button>
              )}
              {activeTab === 3 && (
                <Button
                  type="button"
                  color="primary"
                  onClick={() => {
                    if (handlers.checkForNullValues()) {
                      setModalErrorVisible(true);
                    } else {
                      setModalFinishVisible(true);
                    }
                  }}
                  itemID="btn_nextTab"
                  disabled={isFinished && isSaveDisabled}
                >
                  Finish
                </Button>
              )}
            </div>

            <Modal
              visible={modalVisible}
              setVisible={setModalVisible}
              actions={actions}
              title={t('saveChanges')}
            >
              {t('dirtyIndicatorsPopupMsg')}
            </Modal>

            <Modal
              visible={modalFinishVisible}
              setVisible={setModalFinishVisible}
              actions={actionsFinish}
              title={t('Finish Calculator')}
            >
              {t('finishedCalculatorPopupMsg')}
            </Modal>

            <Modal
              visible={modalErrorVisible}
              setVisible={setModalErrorVisible}
              title={t('Error')}
              actions={actionsNoFinish}
            >
              {t('Cannot Finish a calculator without any Construction or Operation values.')}
            </Modal>
          </>
        )}
      </SpinnerArea>
    );
  }
);

export default SectorTabContainerBase;
